import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Scanner from './components/Scanner';
import MobileNav from './components/MobileNav';
import { Box, LayoutDashboard, Settings, User, Scan, Menu, X, FolderOpen, Video, ClipboardList, StickyNote, BookOpen } from 'lucide-react';
import { Button } from './components/ui/button';
import { cn } from './lib/utils';
import './App.css';

// Lazy load pages for better initial load time
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const ContainerList = lazy(() => import('./pages/ContainerList'));
const ContainerDetail = lazy(() => import('./pages/ContainerDetail'));
const NewContainer = lazy(() => import('./pages/NewContainer'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const CategoryManagement = lazy(() => import('./pages/CategoryManagement'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement'));
const FolderManagement = lazy(() => import('./pages/FolderManagement'));
const FolderBrowser = lazy(() => import('./pages/FolderBrowser'));
const VideoBrowser = lazy(() => import('./pages/VideoBrowser'));
const Checklists = lazy(() => import('./pages/Checklists'));
const Notes = lazy(() => import('./pages/Notes'));
const Rapport = lazy(() => import('./pages/Rapport'));
const MaintenanceHistory = lazy(() => import('./pages/MaintenanceHistory'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const DataImport = lazy(() => import('./pages/DataImport'));
const Profile = lazy(() => import('./pages/Profile'));

// Loading spinner for lazy loaded components
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main Layout with navigation
const MainLayout = ({ children }) => {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Box, label: 'Container', path: '/containers' },
    { icon: FolderOpen, label: 'Ordner', path: '/folders' },
    { icon: Video, label: 'Videos', path: '/videos' },
    { icon: ClipboardList, label: 'Checklisten', path: '/checklists' },
    ...(isAdmin() ? [{ icon: Settings, label: 'Admin', path: '/admin' }] : []),
    { icon: User, label: 'Profil', path: '/profile' },
    { icon: StickyNote, label: 'Notizen', path: '/notes' },
    { icon: BookOpen, label: 'Rapport', path: '/rapport' },
  ];

  const handleScanComplete = (result) => {
    // Optionally refresh data after scan
    console.log('Scan completed:', result);
  };

  return (
    <div className="app-container min-h-screen bg-slate-100">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 flex-col z-30">
        {/* Logo */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Box className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="heading font-bold text-slate-900">TPS</h1>
              <p className="text-xs text-slate-500">by Steripoint</p>
            </div>
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                  isActive
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-slate-600 hover:bg-slate-50'
                )}
                data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.username}</p>
              <p className="text-xs text-slate-500">{isAdmin() ? 'Admin' : 'Benutzer'}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-30">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Box className="h-4 w-4 text-emerald-600" />
            </div>
            <span className="heading font-bold text-slate-900">TPS</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-slate-200 shadow-lg">
            <nav className="p-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left',
                      isActive ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="md:ml-64 pt-14 md:pt-0 pb-24 md:pb-8 min-h-screen">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Navigation */}
      <MobileNav onScanClick={() => setScannerOpen(true)} />

      {/* Scanner Overlay */}
      <Scanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanComplete={handleScanComplete}
      />

      {/* Toast Notifications */}
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{
          className: 'font-sans',
        }}
      />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/containers" element={<ContainerList />} />
                        <Route path="/containers/:id" element={<ContainerDetail />} />
                        <Route path="/folders" element={<FolderBrowser />} />
                        <Route path="/videos" element={<VideoBrowser />} />
                        <Route path="/checklists" element={<Checklists />} />
                        <Route path="/notes" element={<Notes />} />
                        <Route path="/rapport" element={<Rapport />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute adminOnly>
                              <AdminPanel />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/containers/new"
                          element={<NewContainer />}
                        />
                        <Route
                          path="/admin/categories"
                          element={
                        <ProtectedRoute adminOnly>
                          <CategoryManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/companies"
                      element={
                        <ProtectedRoute adminOnly>
                          <CompanyManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/folders"
                      element={
                        <ProtectedRoute adminOnly>
                          <FolderManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/maintenance-history"
                      element={
                        <ProtectedRoute adminOnly>
                          <MaintenanceHistory />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute adminOnly>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/import"
                      element={
                        <ProtectedRoute adminOnly>
                          <DataImport />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                    </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
